/**
 * Life4Earth V2 — React hook for user knowledge (blog) contributions.
 *
 * Loads published blog articles authored by a user.
 * Used on the Impact tab to show "My Knowledge" section.
 *
 * Pattern follows useApiUserChronicles.ts / useApiUserRoles.ts.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchUserArticles } from './blogArticles';
import type { BlogArticleSummary } from './blogArticles';
import { fetchForKey, CACHE_UPDATE_EVENT } from './queryCache';

// ── Result shape ────────────────────────────────────────────

export interface UseApiUserKnowledgeResult {
  articles: BlogArticleSummary[];
  loading: boolean;
  error: string | null;
  source: 'api' | 'empty' | 'loading';
  /** Force a refetch (e.g. after publishing a new article). */
  refetch: () => void;
}

// ── Hook ────────────────────────────────────────────────────

/**
 * Load published blog articles for a user.
 * Pass null/undefined userGuid to skip loading.
 */
export function useApiUserKnowledge(
  userGuid: string | null | undefined,
): UseApiUserKnowledgeResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiUserKnowledgeResult>({
    articles: [],
    loading: true,
    error: null,
    source: 'loading',
    refetch: () => {},
  });

  const cacheKey = userGuid ? `userKnowledge:${userGuid}` : null;

  const refetch = useCallback(async () => {
    if (!mountedRef.current || !userGuid) return;

    try {
      const articles = await fetchForKey(
        cacheKey!,
        () => fetchUserArticles(userGuid),
        { force: true },
      );

      if (!mountedRef.current) return;

      setData({
        articles,
        loading: false,
        error: null,
        source: 'api',
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load articles',
        source: 'api',
        refetch,
      }));
    }
  }, [userGuid, cacheKey]);

  // Initial load
  useEffect(() => {
    mountedRef.current = true;
    if (userGuid) {
      refetch();
    } else {
      setData({
        articles: [],
        loading: false,
        error: null,
        source: 'empty',
        refetch,
      });
    }
    return () => {
      mountedRef.current = false;
    };
  }, [userGuid, refetch]);

  // Listen for cache invalidation events (SignalR-driven)
  useEffect(() => {
    if (!cacheKey) return;

    const handleCacheUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === cacheKey) {
        refetch();
      }
    };

    window.addEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
  }, [cacheKey, refetch]);

  return data;
}
